/**
 * What every tab of this browser knows about its session: one `localStorage` entry,
 * `sessionMarker`.
 *
 * Each tab runs its own idle countdown. A tab the browser froze, or that slept with the computer,
 * wakes with its old deadline, and without the messages other tabs sent meanwhile. Before it logs
 * out, it reads this entry, to see whether another tab kept the session alive or someone has logged
 * in since. It can't ask the server instead: any request with the session cookie extends the
 * session.
 *
 * An active marker holds the opaque session instance identifier that the server's activity ping
 * sends, and when the session is due to expire on this browser's clock. Neither identifies the
 * user. The identifier is only compared for equality between tabs.
 *
 * Every marker also has a generation. Starting a login or logout advances it and leaves a
 * transition marker, saying which, until the first ping for the resulting session. A ping may
 * update the marker only while the generation it captured before its request is still current, so a
 * late response from the old session can't overwrite the new one.
 *
 * A login or explicit-logout transition stops a tab's automatic logout. A plain logout transition
 * is another tab automatically ending the same session. Each tab whose countdown runs out still
 * saves its place and tries that guarded logout, so it can't return to a session that's about to
 * end. An explicit logout instead clears every tab's place and must not lose a race to an idle one.
 *
 * A tab that has no current or saved session instance identifier cannot prove which session it
 * belongs to, so it returns through Resume instead of risking ending a newer session.
 */

const STORAGE_KEY = 'sessionMarker'

/**
 * The last session this tab learned, kept across its page loads so that a logout started before a
 * new page learns its own session can still be guarded. Unlike `sessionMarker`, it isn't shared by
 * tabs.
 */
const TAB_STORAGE_KEY = 'sessionInstanceId'

const VERSION = 1
const SESSION_INSTANCE_ID_MIN_LENGTH = 32
const SESSION_INSTANCE_ID_MAX_LENGTH = 64
const SESSION_INSTANCE_ID_PATTERN = /^si\.[A-Za-z0-9+/_=.:^-]+$/

interface MarkerBase {
  v: typeof VERSION
  generation: number
}

export interface ActiveSessionMarker extends MarkerBase {
  kind: 'active'
  /** An opaque server value identifying this particular session. Only compared for equality. */
  sessionInstanceId: string
  /** When the session is due to expire, on this browser's clock. */
  expiresAt: number
}

/** What started a transition. */
export type TransitionCause =
  /** A tab is going to the identity provider to log in. */
  | 'login'
  /** A tab is ending the session after an idle timeout or a failed ping. */
  | 'logout'
  /** The user chose Log out, which also clears every tab's saved place. */
  | 'explicitLogout'

export interface TransitionSessionMarker extends MarkerBase {
  /**
   * A login or logout has started, so no response from the preceding generation may act on the
   * browser's current session.
   */
  kind: 'transition'
  cause: TransitionCause
}

export type SessionMarker = ActiveSessionMarker | TransitionSessionMarker

/** Identifies the marker generation at the moment an activity request starts. */
export interface SessionPing {
  /** 0 when there was no marker. */
  generation: number
  /**
   * The session the calling page observed before the request, if any. A response naming another
   * session means the page is out of date.
   */
  sessionInstanceId: string | undefined
  /**
   * The session a logout started from this request should name in its guard: the page's, or else
   * the last one this tab learned, possibly on an earlier page. It never marks a page out of date.
   */
  guardSessionInstanceId: string | undefined
}

/** The session a page has itself seen a ping answer for, or undefined before its first one. */
export interface ObservingPage {
  sessionInstanceId: string | undefined
}

/** What {@link noteSessionPing} did with a ping's response. */
export type PingRecord =
  /** The marker now describes the session the ping answered for. */
  | 'recorded'
  /** A login or logout began after the request started. Its response mustn't be acted on. */
  | 'superseded'
  /** The request used a session different from the one this tab's page had observed. */
  | 'newSession'
  /** There was nothing to record, for example no session instance identifier. */
  | 'ignored'

/** What a tab whose idle countdown has run out should do. */
export type ExpiredCountdown =
  /** Another tab kept this tab's session alive. Wait until `expiresAt` instead. */
  | { kind: 'extended'; expiresAt: number }
  /**
   * Someone has logged in, or started to, since this tab's session ended. A logout could end the
   * new session, so the tab goes to the Resume page without one.
   */
  | { kind: 'newSession' }
  /** The user explicitly logged out in another tab. Clear this tab's saved place and wait. */
  | { kind: 'explicitLogout' }
  /** The session has expired, or is being logged out: an idle logout, claiming `generation`. */
  | { kind: 'expired'; generation: number | undefined }

/**
 * Starts an activity request. The result must be given to {@link noteSessionPing}.
 *
 * A page that tracks its own session passes `page`. A freshly loaded page has seen none yet, even
 * if the tab remembers one from an earlier page, for example a reopened tab or a restored browser
 * session: its content already belongs to whichever session its first ping answers for. Without
 * `page`, the last session this tab learned stands in.
 */
export function beginSessionPing(page?: ObservingPage): SessionPing {
  const tabSessionInstanceId = readTabSession()
  return {
    generation: readSessionMarker()?.generation ?? 0,
    sessionInstanceId: page ? page.sessionInstanceId : tabSessionInstanceId,
    guardSessionInstanceId: page?.sessionInstanceId ?? tabSessionInstanceId,
  }
}

/**
 * Records a successful activity ping, unless a login or logout began after the request started.
 * Out-of-order responses for one session may extend its deadline, but never shorten it.
 */
export function noteSessionPing(
  ping: SessionPing,
  sessionInstanceId: string | null | undefined,
  expirySeconds: number,
  now = Date.now(),
): PingRecord {
  const current = readSessionMarker()
  if (
    ping.sessionInstanceId !== undefined &&
    isSessionInstanceId(sessionInstanceId) &&
    ping.sessionInstanceId !== sessionInstanceId
  ) {
    return 'newSession'
  }
  if (isSuperseded(ping, current, sessionInstanceId)) {
    return 'superseded'
  }
  if (!isFiniteNumber(expirySeconds)) {
    return 'ignored'
  }
  if (!isSessionInstanceId(sessionInstanceId)) {
    // No session identifier and no time left: the server has no session. Record that state instead
    // of waiting for a deadline another tab once saw.
    if (expirySeconds <= 0 && current?.kind === 'active') {
      markSessionTransition('logout')
      forgetTabSession()
      return 'recorded'
    }
    return 'ignored'
  }
  if (
    current?.kind === 'active' &&
    current.sessionInstanceId === sessionInstanceId &&
    current.expiresAt >= now + expirySeconds * 1000
  ) {
    writeTabSession(sessionInstanceId)
    return 'recorded'
  }
  write({
    v: VERSION,
    generation: current?.generation ?? 1,
    kind: 'active',
    sessionInstanceId,
    expiresAt: now + expirySeconds * 1000,
  })
  writeTabSession(sessionInstanceId)
  return 'recorded'
}

function isSuperseded(
  ping: SessionPing,
  current: SessionMarker | undefined,
  responseSessionInstanceId: string | null | undefined,
): boolean {
  if (!current) {
    return false
  }
  // From no marker, generation 1 can be another tab's first ping rather than a transition. Only a
  // response that names that active session may update it; a response with no identifier could be
  // a stale, session-less ping from before another tab logged in.
  if (ping.generation === 0) {
    return (
      current.kind === 'transition' ||
      current.generation > 1 ||
      (current.kind === 'active' &&
        (!isSessionInstanceId(responseSessionInstanceId) ||
          current.sessionInstanceId !== responseSessionInstanceId))
    )
  }
  return current.generation !== ping.generation
}

/** Invalidates every request and countdown that began before this login or logout. */
export function markSessionTransition(cause: TransitionCause): number {
  if (cause === 'login' || cause === 'explicitLogout') {
    forgetTabSession()
  }
  const generation = (readSessionMarker()?.generation ?? 0) + 1
  write({ v: VERSION, generation, kind: 'transition', cause })
  return generation
}

/**
 * Claims an automatic logout of the session a tab knows by its opaque identifier. A generation
 * alone cannot prove which session the tab belongs to. Returns the claimed generation, or undefined
 * when the tab has no identifier, a login has started, or a different session is active. Another
 * logout of the same session doesn't stop it.
 */
export function claimSessionTransition(sessionInstanceId: string | undefined): number | undefined {
  if (sessionInstanceId === undefined) {
    return undefined
  }
  const current = readSessionMarker()
  if (
    current?.kind === 'transition' &&
    (current.cause === 'login' || current.cause === 'explicitLogout')
  ) {
    return undefined
  }
  if (current?.kind === 'active') {
    if (current.sessionInstanceId !== sessionInstanceId) {
      return undefined
    }
  }
  return markSessionTransition('logout')
}

export type AutomaticLogoutSuperseded = 'login' | 'explicitLogout' | 'newSession'

/**
 * Whether an explicit logout that never learned its session must stop before it writes a
 * transition. `observed` is the marker from before the request. A login, or a different active
 * session, that arrived while the request was in flight is not this tab's to replace. An unchanged
 * marker, including none, is not a reason to stop: this tab cannot name a session, so it cannot
 * tell an older marker from its own.
 */
export function explicitLogoutSupersededSince(
  observed: SessionMarker | undefined,
): AutomaticLogoutSuperseded | undefined {
  const current = readSessionMarker()
  if (
    current?.kind === 'transition' &&
    (current.cause === 'login' || current.cause === 'explicitLogout') &&
    !sameMarker(current, observed)
  ) {
    return current.cause
  }
  if (
    current?.kind === 'active' &&
    (observed?.kind !== 'active' || observed.sessionInstanceId !== current.sessionInstanceId)
  ) {
    return 'newSession'
  }
  return undefined
}

/**
 * Whether a login or another explicit logout superseded an explicit-logout claim. An active marker
 * for the session being ended may be a ping that began after the claim; any other active session is
 * a login that completed later. Without an identifier, no active session can safely be treated as
 * the one this tab ended.
 */
export function claimedExplicitLogoutSupersededSince(
  generation: number,
  sessionInstanceId: string | undefined,
): AutomaticLogoutSuperseded | undefined {
  const current = readSessionMarker()
  if (current?.kind === 'transition' && current.generation >= generation) {
    if (current.cause === 'login') {
      return 'login'
    }
    if (current.cause === 'explicitLogout' && current.generation > generation) {
      return 'explicitLogout'
    }
  }
  if (
    current?.kind === 'active' &&
    (sessionInstanceId === undefined || current.sessionInstanceId !== sessionInstanceId)
  ) {
    return 'newSession'
  }
  return undefined
}

/** Whether a login began or completed while a logout-continuation response was delayed. */
export function logoutContinuationSuperseded(sessionInstanceId: string | undefined): boolean {
  const current = readSessionMarker()
  if (current?.kind === 'transition') {
    return current.cause === 'login'
  }
  return (
    current?.kind === 'active' &&
    (sessionInstanceId === undefined || current.sessionInstanceId !== sessionInstanceId)
  )
}

function sameMarker(current: SessionMarker, observed: SessionMarker | undefined): boolean {
  if (observed?.kind !== current.kind || observed.generation !== current.generation) {
    return false
  }
  if (current.kind === 'transition' && observed.kind === 'transition') {
    return observed.cause === current.cause
  }
  return (
    current.kind === 'active' &&
    observed.kind === 'active' &&
    observed.sessionInstanceId === current.sessionInstanceId
  )
}

/** Why an automatic logout must stop after it claimed `generation`, if it must. */
export function automaticLogoutSupersededSince(
  generation: number,
  sessionInstanceId: string | undefined,
): AutomaticLogoutSuperseded | undefined {
  const current = readSessionMarker()
  if (current?.kind === 'transition' && current.generation >= generation) {
    if (current.cause === 'login' || current.cause === 'explicitLogout') {
      return current.cause
    }
  }
  if (
    current?.kind === 'active' &&
    sessionInstanceId !== undefined &&
    current.sessionInstanceId !== sessionInstanceId
  ) {
    return 'newSession'
  }
  return undefined
}

/**
 * Advances a completed logout's generation once more, invalidating pings that started while the
 * logout request was in flight. A login, or another logout, that already advanced the marker is
 * left alone.
 */
export function finishSessionTransition(generation: number) {
  const current = readSessionMarker()
  if (current === undefined || current.generation === generation) {
    markSessionTransition(
      current?.kind === 'transition' && current.cause === 'explicitLogout'
        ? 'explicitLogout'
        : 'logout',
    )
  }
}

/**
 * Whether someone has logged in, or started to, since this tab learned its session,
 * `sessionInstanceId`. Without one, the tab can't tell.
 */
export function hasNewSession(sessionInstanceId: string | undefined): boolean {
  if (sessionInstanceId === undefined) {
    return false
  }
  const marker = readSessionMarker()
  if (marker?.kind === 'transition') {
    return marker.cause === 'login'
  }
  return marker !== undefined && marker.sessionInstanceId !== sessionInstanceId
}

/**
 * Decides what a tab does when its idle countdown runs out, from the session it last saw and the
 * deadline it was counting down to.
 */
export function checkExpiredCountdown(
  sessionInstanceId: string | undefined,
  deadline: number | undefined,
  now = Date.now(),
): ExpiredCountdown {
  const marker = readSessionMarker()
  if (marker?.kind === 'transition' && marker.cause === 'explicitLogout') {
    return { kind: 'explicitLogout' }
  }
  if (sessionInstanceId === undefined) {
    return { kind: 'newSession' }
  }
  if (!marker) {
    return { kind: 'expired', generation: undefined }
  }
  if (hasNewSession(sessionInstanceId)) {
    return { kind: 'newSession' }
  }
  // Another tab is logging this session out, or has: logging out too is harmless.
  if (marker.kind === 'transition' || marker.expiresAt <= now) {
    return { kind: 'expired', generation: marker.generation }
  }
  // Only a later deadline than this tab's: the same one would only count down again.
  if (deadline === undefined || marker.expiresAt > deadline) {
    return { kind: 'extended', expiresAt: marker.expiresAt }
  }
  return { kind: 'expired', generation: marker.generation }
}

/**
 * The entry, or undefined. It's same-origin writable, so it's checked like any other input. An
 * entry that fails the check is removed.
 */
export function readSessionMarker(): SessionMarker | undefined {
  const raw = readRaw()
  if (raw === null) {
    return undefined
  }
  const marker = parse(raw)
  if (!marker) {
    remove()
  }
  return marker
}

/** Forgets the last session this tab learned. */
export function forgetTabSession() {
  try {
    window.sessionStorage.removeItem(TAB_STORAGE_KEY)
  } catch {
    // Storage unavailable. The shared marker and server guard still protect automatic logout.
  }
}

/** The last session this tab learned, possibly on an earlier page. */
export function readTabSession(): string | undefined {
  try {
    const value = window.sessionStorage.getItem(TAB_STORAGE_KEY)
    if (value === null) {
      return undefined
    }
    return isSessionInstanceId(value) ? value : undefined
  } catch {
    return undefined
  }
}

function writeTabSession(sessionInstanceId: string) {
  try {
    window.sessionStorage.setItem(TAB_STORAGE_KEY, sessionInstanceId)
  } catch {
    // Storage unavailable. UserActivityMonitor retains the same value in memory.
  }
}

function write(marker: SessionMarker) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(marker))
  } catch {
    // Storage unavailable. Tabs then log out when their countdowns run out, as before.
  }
}

function remove() {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage unavailable. There's nothing to remove.
  }
}

function readRaw(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function parse(raw: string): SessionMarker | undefined {
  let value: unknown
  try {
    value = JSON.parse(raw)
  } catch {
    return undefined
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return undefined
  }
  const { v, generation, kind, cause, sessionInstanceId, expiresAt } = value as Record<
    string,
    unknown
  >
  if (v !== VERSION || !Number.isSafeInteger(generation) || (generation as number) < 1) {
    return undefined
  }
  const base = { v: VERSION, generation: generation as number } as const
  if (kind === 'transition') {
    return cause === 'login' || cause === 'logout' || cause === 'explicitLogout'
      ? { ...base, kind, cause }
      : undefined
  }
  if (kind !== 'active' || !isSessionInstanceId(sessionInstanceId) || !isFiniteNumber(expiresAt)) {
    return undefined
  }
  return { ...base, kind, sessionInstanceId, expiresAt }
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

/**
 * The contents are opaque; validate only the API's documented length and transport-safe alphabet.
 */
function isSessionInstanceId(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length >= SESSION_INSTANCE_ID_MIN_LENGTH &&
    value.length <= SESSION_INSTANCE_ID_MAX_LENGTH &&
    SESSION_INSTANCE_ID_PATTERN.test(value)
  )
}
